import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API_BASE } from "@/lib/utils";
import { AUTH_SESSION_KEY, getAuthSession } from "@/lib/auth-session";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { useTheme } from "@/components/ThemeProvider";
import {
  DEFAULT_BRAND,
  applyBrandDocumentEffects,
  brandCssVars,
  clearTenantDocumentEffects,
  normalizePublicBrand,
} from "@/lib/tenant-brand";

const TenantBrandContext = createContext({
  brand: DEFAULT_BRAND,
  workspaceName: "",
  plan: "studio",
  canEditWhitelabel: false,
  loading: true,
  refreshBrand: async () => {},
});

export function useTenantBrand() {
  return useContext(TenantBrandContext);
}

export default function TenantBrandProvider({
  children,
  scope = "admin",
  tenantQuery = "",
  className = "aivory-tenant-brand flex flex-col flex-1 min-h-0 min-w-0",
  applyDocumentEffects = true,
}) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [workspaceName, setWorkspaceName] = useState("");
  const [plan, setPlan] = useState("studio");
  const [canEditWhitelabel, setCanEditWhitelabel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState("");

  const refreshBrand = useCallback(async () => {
    setLoading(true);
    try {
      if (scope === "admin") {
        const res = await fetch(`${API_BASE}/api/admin/workspace/brand`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const whitelabelOk = Boolean(data.canEditWhitelabel || data.whitelabelEntitled);
        const b = data.branding || {};
        setTenantId(String(data.tenantId || getAuthSession()?.tenant || ""));
        setCanEditWhitelabel(whitelabelOk);
        setBrand(
          normalizePublicBrand(
            whitelabelOk && b.enabled
              ? {
                  enabled: b.enabled,
                  mode: b.mode,
                  productName: b.productName || data.workspaceName,
                  logoUrl: b.logoUrl,
                  faviconUrl: b.faviconUrl,
                  colors: b.colors,
                  fonts: b.fonts,
                  showPoweredBy: b.showPoweredBy,
                  customCss: b.customCss,
                }
              : { enabled: false },
          ),
        );
        setWorkspaceName(String(data.workspaceName || ""));
        setPlan(String(data.plan || "studio"));
      } else {
        const host = typeof window !== "undefined" ? window.location.hostname : "";
        const qs = new URLSearchParams();
        if (tenantQuery) qs.set("tenant", tenantQuery);
        if (host) qs.set("host", host);
        const res = await fetch(`${API_BASE}/api/public/tenant-brand?${qs.toString()}`);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        setTenantId(String(data.tenantId || ""));
        setBrand(normalizePublicBrand(data.brand));
        setWorkspaceName(String(data.brand?.productName || ""));
        setPlan("public");
        setCanEditWhitelabel(false);
      }
    } catch {
      setTenantId(String(getAuthSession()?.tenant || ""));
      setBrand(DEFAULT_BRAND);
    } finally {
      setLoading(false);
    }
  }, [scope, tenantQuery]);

  useEffect(() => {
    refreshBrand();
  }, [refreshBrand]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === AUTH_SESSION_KEY) refreshBrand();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshBrand]);

  const { theme } = useTheme();

  useEffect(() => {
    if (!applyDocumentEffects) return undefined;
    applyBrandDocumentEffects(brand, { tenantId, faviconKey: tenantId || undefined }, { uiTheme: theme });
    return () => clearTenantDocumentEffects();
  }, [brand, tenantId, applyDocumentEffects, theme]);

  const cssVars = useMemo(
    () => brandCssVars(brand, { respectUiTheme: true, uiTheme: theme }),
    [brand, theme],
  );

  const value = useMemo(
    () => ({
      brand: normalizePublicBrand(brand),
      workspaceName,
      plan,
      canEditWhitelabel,
      loading,
      refreshBrand,
    }),
    [brand, workspaceName, plan, canEditWhitelabel, loading, refreshBrand],
  );

  return (
    <TenantBrandContext.Provider value={value}>
      <div className={className} style={cssVars} data-testid="tenant-brand-scope">
        {children}
      </div>
    </TenantBrandContext.Provider>
  );
}
